import { z } from "zod";
import { IsoDateTime, OpaqueId } from "./common.js";
/**
 * Proposition de régulation (CDC §5, backlog Phase 2 P2-18). **Sentinelle
 * conseille, n'agit pas** : aucune de ces options n'est appliquée, aucun verbe
 * mutant. Le régulateur lit, décide, et fait par ses moyens habituels (radio,
 * SAEIV embarqué).
 */
export const RegulationOptionKind = z.enum([
    "hold", // tenir au terminus (résorber une avance / desserrer un bunching)
    "early-departure", // la course repart sans battement pour limiter le retard
    "next-early-departure", // la course suivante part en avance pour combler le trou
    "turnback", // retournement à un arrêt intermédiaire (§5.7)
    "skip-ahead", // saut d'une portion (HLP partiel) via un parcours alternatif
    "deviation", // contournement d'un point bloqué via un parcours alternatif
]);
/** Relève / pause légale menacée par le retard (détection automatique). */
export const ReleveRisk = z.object({
    atSeconds: z.number().int(), // heure de la relève (secondes depuis minuit local)
    placeRaw: z.string(),
    placeName: z.string(),
    /** Marge restante (s) avant que la relève soit ratée — négatif = déjà ratée au rythme actuel. */
    marginSeconds: z.number().int(),
    /** `true` si une coupure du plan de service tombe sous `thresholds.minLegalBreakSeconds`. */
    legalBreakAtRisk: z.boolean(),
});
export const RegulationOption = z.object({
    kind: RegulationOptionKind,
    label: z.string(), // « Tenir 2 min 30 au terminus Boulingrin »
    feasibility: z.enum(["feasible", "uncertain", "infeasible"]),
    /** Effet net sur le **retard de la course** (s ; positif = résorbe). `null` = non chiffré. */
    deviationEffectSeconds: z.number().int().nullable(),
    /** Intervalle estimé (s) avec le véhicule **de devant** après application. `null` = inconnu. */
    headwayAheadSeconds: z.number().int().nullable(),
    /** Intervalle estimé (s) avec le véhicule **de derrière** après application. */
    headwayBehindSeconds: z.number().int().nullable(),
    detail: z.string().nullable(), // « via f1-contournement-diochon, saute 3 arrêts » / motif si infeasible
    atStopId: OpaqueId.nullable(), // point concerné (retournement, entrée de parcours)
    // v1.44 — chiffrage fin du solveur de rattrapage (présents sur `turnback` /
    // `skip-ahead` quand OSRM + horaire sens inverse sont dispo ; absents sinon).
    recoverySeconds: z.number().int().optional(), // retard réellement absorbé
    resultingDeviationSeconds: z.number().int().optional(), // écart de la course APRÈS la manœuvre
    catchesDepartureAt: IsoDateTime.optional(), // départ théorique (sens inverse) rejoint
    stopsNotServedCount: z.number().int().optional(), // arrêts sautés par la manœuvre
});
/**
 * Solveur de rattrapage (v1.44) — évalue, pour une course en retard, les deux
 * manœuvres possibles et chiffre ce qu'elles absorbent, en croisant l'horaire
 * théorique du **sens inverse** (le départ que le véhicule « devient ») avec le
 * temps routier réel (OSRM) du haut-le-pied.
 */
export const RecoveryOption = z.object({
    pattern: z.enum([
        "turnback", // retournement à un arrêt intermédiaire : abandonne la queue de course
        "continue-then-hlp", // finit la course, puis HLP du terminus jusqu'à un arrêt du sens inverse
    ]),
    atStopId: OpaqueId, // arrêt de retournement, ou arrêt de reprise commerciale après HLP
    atStopName: z.string(),
    hlpSeconds: z.number().int().nonnegative().nullable(), // temps routier du haut-le-pied (OSRM)
    hlpSource: z.enum(["osrm", "none"]),
    readyAt: IsoDateTime, // instant où le véhicule est prêt à repartir en commercial
    catchesTripId: z.string().nullable(), // course théorique (sens inverse) rejointe
    catchesDepartureAt: IsoDateTime.nullable(),
    resultingDeviationSeconds: z.number().int().nullable(), // écart après la manœuvre (négatif = en avance ⇒ attend)
    recoverySeconds: z.number().int().nullable(), // retard absorbé = deviationSeconds − max(0, resulting)
    stopsNotServedCount: z.number().int().nonnegative(),
    outcome: z.enum(["full", "partial", "none"]), // rattrape tout / une partie / rien d'exploitable
    feasibility: z.enum(["feasible", "uncertain"]),
});
export const RecoveryPlan = z.object({
    journeyId: OpaqueId,
    computedAt: IsoDateTime,
    deviationSeconds: z.number().int(),
    best: RecoveryOption.nullable(), // la manœuvre la plus profitable, ou `null` si aucune n'aide
    options: z.array(RecoveryOption), // triées : rattrapage décroissant
});
export const RegulationOptionsResponse = z.object({
    journeyId: OpaqueId,
    computedAt: IsoDateTime,
    deviationSeconds: z.number().int(),
    situation: z.enum(["late", "early", "bunching", "releve-risk", "on-time"]),
    releveRisk: ReleveRisk.nullable(),
    options: z.array(RegulationOption), // les plus utiles d'abord
});
//# sourceMappingURL=regulation.js.map